import ConnectingAirportsIcon from "@mui/icons-material/ConnectingAirports";
import InfoIcon from "@mui/icons-material/Info";
import PictureInPictureIcon from "@mui/icons-material/PictureInPicture";
import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useMultiHangarState } from "../../containers/MultiHangarContainer";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { getStackPolygons, removeNonNumericAndParse } from "../../hooks/utils";
import { FBO, Hangar } from "../../types";
import { formatNumber } from "../../utils";
import { sum } from "../../utils/lodash-replacements";
import { squareFeetToSquareMeters } from "../../utils/math";
import { getRoleSetting } from "../../utils/permissions";
import { HangarIcon } from "../../widgets/Icons/HangarIcon";
import { SexyText } from "../../widgets/SexyText/SexyText";
import {
  calculateTotalArea,
  calculateUtilization,
  findTenantsFullyInHangar,
} from "../Hangar/HangarInfoCard";
import {
  calcEstimateTowTime,
  estimateValueBreakdown,
} from "../Hangars/HangarsPresenter";
import { AircraftFinder } from "../Ramp/AircraftFinder";
import { useLayout } from "../Ramp/CollapsibleRampActionCard";
import { MicroMetric } from "../Ramp/RampInfoCard";
import { HOLDING_AREA_ID } from "./HoldingArea";

export const calculateMultiHangarStats = (
  activeFBO: FBO,
  allHangars: Hangar[]
) => {
  const hangars = allHangars.filter((h) => h.id !== HOLDING_AREA_ID);
  const polygons = hangars.map((hangar) =>
    getStackPolygons(hangar.stack, hangar.width, 1, true)
  );
  const nAircraftInHangar = sum(
    hangars.map((hangar, idx) => {
      const entityPolygons = polygons[idx];
      return findTenantsFullyInHangar(hangar, entityPolygons).length;
    })
  );

  const nAircraft = sum(hangars.map((hangar) => hangar.stack.tenants.length));
  const totalSqftAvailable = sum(
    hangars.map((hangar) =>
      calculateTotalArea(
        hangar,
        activeFBO?.settings?.ignoreDeadSpaceForUtilization ?? false
      )
    )
  );
  const totalSqftUsed = sum(
    hangars.map((hangar, idx) => {
      const entityPolygons = polygons[idx];
      return calculateUtilization(
        hangar,
        entityPolygons,
        true,
        activeFBO?.settings?.ignoreDeadSpaceForUtilization ?? false,
        true
      );
    })
  );

  const totalSqftDesired = sum(
    hangars.map((hangar, idx) => {
      const entityPolygons = polygons[idx];
      return calculateUtilization(
        hangar,
        entityPolygons,
        null,
        activeFBO?.settings?.ignoreDeadSpaceForUtilization ?? false,
        true
      );
    })
  );
  const totalUtilization = Math.round(
    (totalSqftUsed / totalSqftAvailable) * 100
  );

  const totalUtilizationDesired = Math.round(
    (totalSqftDesired / totalSqftAvailable) * 100
  );

  // Calculate breakdown values
  let estValueBase = 0;
  let estValueTransient = 0;
  hangars.forEach((hangar, idx) => {
    const entityPolygons = polygons[idx];
    const breakdown = estimateValueBreakdown(activeFBO, hangar, entityPolygons);
    estValueBase += breakdown.baseValue;
    estValueTransient += breakdown.transientValue;
  });
  const estValueTotal = estValueBase + estValueTransient;

  const towTime = sum(
    hangars.map((hangar, idx) => {
      const entityPolygons = polygons[idx];
      return removeNonNumericAndParse(
        calcEstimateTowTime(hangar, entityPolygons)
      );
    })
  );

  return {
    nAircraftInHangar,
    nAircraft,
    totalSqftAvailable,
    totalSqftUsed,
    totalSqftDesired,
    totalUtilization,
    totalUtilizationDesired,
    estValueBase,
    estValueTransient,
    estValueTotal,
    towTime,
  };
};

type Props = {};

export const MultiHangarInfoCard: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { isDrawerOpen } = useLayout();
  const displayEstimatedValue = getRoleSetting(activeFBO, 'displayEstimatedValue', true);
  const { hangars, setHangars, setActiveLocationId } = useMultiHangarState();

  const {
    nAircraftInHangar,
    nAircraft,
    totalSqftAvailable,
    totalSqftUsed,
    totalUtilization,
    totalUtilizationDesired,
    estValueBase,
    estValueTransient,
    estValueTotal,
    towTime,
  } = useDebouncedValue(
    () => calculateMultiHangarStats(activeFBO, hangars),
    100,
    [[hangars.map((h) => h.stack)]]
  );

  const totalAreaAvailable = activeFBO?.settings?.useMetricSystem
    ? squareFeetToSquareMeters(totalSqftAvailable)
    : totalSqftAvailable;

  const totalAreaUsed = activeFBO?.settings?.useMetricSystem
    ? squareFeetToSquareMeters(totalSqftUsed)
    : totalSqftUsed;

  const allTenants = React.useMemo(
    () =>
      hangars
        .filter((h) => h.id !== HOLDING_AREA_ID)
        .flatMap((hangar) => hangar.stack.tenants),
    [hangars.map((h) => h.stack.tenants)]
  );

  if (!isDrawerOpen) {
    return (
      <>
        <Stack direction="column" alignItems="center">
          <Typography variant="caption">{t('screens.multiHangar.multiHangarInfoCard.title')}</Typography>
          <Stack
            direction="column"
            justifyContent="center"
            spacing={1}
            sx={{
              p: 1,
              pt: 0,
              maxHeight: "100%",
            }}
          >
            <MicroMetric
              icon={<HangarIcon />}
              value={`${
                hangars.filter((h) => h.id !== HOLDING_AREA_ID).length
              } `}
              tooltip={t('screens.multiHangar.multiHangarInfoCard.tooltips.numberOfHangars')}
            />
            <MicroMetric
              icon={<ConnectingAirportsIcon />}
              value={
                <>
                  <span style={{ color: "inherit" }}>
                    <SexyText
                      value={nAircraftInHangar}
                      text={nAircraftInHangar}
                    />{" "}
                  </span>
                  / {nAircraft}
                </>
              }
              tooltip={t('screens.multiHangar.multiHangarInfoCard.tooltips.aircraftCount')}
            />
            <MicroMetric
              icon={<PictureInPictureIcon />}
              value={
                <>
                  <span style={{ color: "inherit" }}>
                    <SexyText
                      value={totalUtilization}
                      text={`${totalUtilization}%`}
                    />{" "}
                  </span>
                  / {totalUtilizationDesired}%
                </>
              }
              tooltip={t('screens.multiHangar.multiHangarInfoCard.tooltips.utilization')}
            />
          </Stack>
        </Stack>
        <Divider />
        <AircraftFinder
          mini={!isDrawerOpen}
          tenants={allTenants ?? []}
          onSelect={(tenant) => {
            const selectedTenantHangar = hangars.find((hangar) =>
              hangar.stack.tenants.find((t) => t.id === tenant.id)
            );
            setActiveLocationId(selectedTenantHangar.id);
            setHangars(
              hangars.map((hangar) => ({
                ...hangar,
                stack: {
                  ...hangar.stack,
                  tenants: hangar.stack.tenants.map((t) => {
                    if (t.id === tenant.id) {
                      return {
                        ...tenant,
                        selected: true,
                      };
                    }
                    return {
                      ...t,
                      selected: false,
                    };
                  }),
                },
              }))
            );
          }}
        />
        <Divider />
      </>
    );
  }

  return (
    <Box
      sx={{
        p: 1,
        borderBottomColor: "info.main",
        borderBottomWidth: 2,
        borderBottomStyle: "solid",
        borderLeft: 1,
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      {/* start stats */}
      <Stack direction="column" spacing={1}>
        <Typography>{t('screens.multiHangar.multiHangarInfoCard.title')}</Typography>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" sx={{ width: "100%" }}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" textAlign="center">
                {t('screens.multiHangar.multiHangarInfoCard.labels.aircraft')}
              </Typography>
              <Tooltip title={t('screens.multiHangar.multiHangarInfoCard.tooltips.aircraftCount')}>
                <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography variant="caption" textAlign="center" color="info.dark">
              <span style={{ color: "inherit" }}>
                <SexyText value={nAircraftInHangar} text={nAircraftInHangar} />{" "}
              </span>
              / {nAircraft}
            </Typography>
          </Stack>
          <Divider orientation="vertical" variant="middle" flexItem />
          <Stack direction="column" sx={{ width: "100%" }}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" textAlign="center">
                {t('screens.multiHangar.multiHangarInfoCard.labels.utilization')}
              </Typography>
              <Tooltip title={t('screens.multiHangar.multiHangarInfoCard.tooltips.utilization')}>
                <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography variant="caption" textAlign="center" color="info.dark">
              <span style={{ color: "inherit" }}>
                <SexyText
                  value={totalUtilization}
                  text={`${totalUtilization}%`}
                />{" "}
              </span>
              / {totalUtilizationDesired}%
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" sx={{ width: "100%" }}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" textAlign="center">
                {t('screens.multiHangar.multiHangarInfoCard.labels.totalSqft')}
              </Typography>
              <Tooltip title={t('screens.multiHangar.multiHangarInfoCard.tooltips.totalSqft')}>
                <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography variant="caption" textAlign="center" color="info.dark">
              <SexyText
                value={totalAreaAvailable}
                text={formatNumber(totalAreaAvailable, 0)}
              />
              {activeFBO?.settings?.useMetricSystem ? " m²" : " ft²"}
            </Typography>
          </Stack>
          <Divider orientation="vertical" variant="middle" flexItem />
          <Stack direction="column" sx={{ width: "100%" }}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" textAlign="center">
                {t('screens.multiHangar.multiHangarInfoCard.labels.totalUtilized')}
              </Typography>
              <Tooltip title={t('screens.multiHangar.multiHangarInfoCard.tooltips.totalUtilized')}>
                <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography variant="caption" textAlign="center" color="info.dark">
              <SexyText
                value={totalAreaUsed}
                text={`${formatNumber(totalAreaUsed, 0)} ${
                  activeFBO?.settings?.useMetricSystem ? " m²" : " ft²"
                }`}
              />
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          {displayEstimatedValue && (
            <>
              <Stack direction="column" sx={{ width: "100%" }}>
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography variant="body2" textAlign="center">
                    {t('screens.multiHangar.multiHangarInfoCard.labels.estValue')}
                  </Typography>
                  <Tooltip title={t('screens.multiHangar.multiHangarInfoCard.tooltips.estValue')}>
                    <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
                  </Tooltip>
                </Stack>
                <Typography
                  variant="caption"
                  textAlign="center"
                  color="info.dark"
                >
                  {t('screens.multiHangar.multiHangarInfoCard.labels.total')}: <SexyText value={estValueTotal} text={`$${estValueTotal} / day`} />
                </Typography>
                <Tooltip title={t('screens.multiHangar.multiHangarInfoCard.tooltips.transientValue')}>
                  <Typography
                    variant="caption"
                    textAlign="center"
                    color="info.dark"
                  >
                    {t('screens.multiHangar.multiHangarInfoCard.labels.transients')}: ${estValueTransient}
                  </Typography>
                </Tooltip>
              </Stack>

              <Divider orientation="vertical" variant="middle" flexItem />
            </>
          )}

          <Stack direction="column" sx={{ width: "100%" }}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" textAlign="center">
                {t('screens.multiHangar.multiHangarInfoCard.labels.estTowTime')}
              </Typography>
              <Tooltip title={t('screens.multiHangar.multiHangarInfoCard.tooltips.estTowTime')}>
                <InfoIcon sx={{ color: "info.dark" }} fontSize="small" />
              </Tooltip>
            </Stack>
            <Typography variant="caption" textAlign="center" color="info.dark">
              {`${towTime} min`}
            </Typography>
          </Stack>
        </Stack>
        <AircraftFinder
          mini={!isDrawerOpen}
          tenants={allTenants ?? []}
          onSelect={(tenant) => {
            const selectedTenantHangar = hangars.find((hangar) =>
              hangar.stack.tenants.find((t) => t.id === tenant.id)
            );
            setActiveLocationId(selectedTenantHangar.id);
            setHangars(
              hangars.map((hangar) => ({
                ...hangar,
                stack: {
                  ...hangar.stack,
                  tenants: hangar.stack.tenants.map((t) => {
                    if (t.id === tenant.id) {
                      return {
                        ...tenant,
                        selected: true,
                      };
                    }
                    return {
                      ...t,
                      selected: false,
                    };
                  }),
                },
              }))
            );
          }}
        />
      </Stack>
    </Box>
  );
};
